import React, { useState, useContext } from 'react';
import { Button, Box, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle } from '@mui/material/';
import AppContext from "../../utils/app-context.js";
import { AuthContext } from "../../utils/auth-provider.js";
import RefManager from '../ref-manager.js';
import { addTakeUserRef } from '../../helpers/index.js';

export default function RefsDialog(props) {
  const { open, close, addRef, shotId, addUserRef = false, callback, totalRefs } = props;
  const { state, dispatch } = useContext(AppContext);
  const { currentUser } = useContext(AuthContext);

  // Staged / pending selection — a Set of take IDs the user has toggled on.
  const [pendingIds, setPendingIds] = useState(new Set());
  // Uploaded takes that were produced during this dialog session (not yet persisted).
  const [pendingTakes, setPendingTakes] = useState({});

  // Reset staged state when dialog opens/closes.
  const handleClose = () => {
    setPendingIds(new Set());
    setPendingTakes({});
    close();
  };

  // Called by RefManager when a new file is uploaded in deferred mode.
  // We auto-select it and cache the take object so Save can find it.
  const handleUpload = ({ take }) => {
    setPendingTakes(prev => ({ ...prev, [take.id]: take }));
    setPendingIds(prev => { const next = new Set(prev); next.add(take.id); return next; });
  };

  // Commit all staged selections to userRefs on Save.
  const handleSave = () => {
    if (pendingIds.size === 0) {
      handleClose();
      return;
    }

    // Resolve each pending ID to a take object.
    const allTakes = [
      ...(state.stories.activeRefList || []),
      ...(state.stories.activeTakeList || []),
      ...Object.values(pendingTakes),
    ];

    const takes = [];
    const seen = new Set();
    for (const id of pendingIds) {
      if (seen.has(id)) continue;
      seen.add(id);
      const take = allTakes.find(t => t.id === id);
      if (take) takes.push(take);
    }

    if (takes.length > 0) {
      addTakeUserRef({
        dispatch,
        state,
        takes,
        currentUser,
        activeStoryRef: state.stories.activeStoryRef,
      });
    }

    if (callback) callback();
    handleClose();
  };

  const pendingCount = pendingIds.size;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      aria-labelledby="refs-dialog-title"
      fullWidth={true}
      maxWidth="md"
      sx={{ width: state.app.isMobileSize ? "100%" : "80vw", margin: "0 auto" }}
    >
      <DialogTitle id="refs-dialog-title">Add References</DialogTitle>
      <DialogContent sx={{ overflow: "auto" }}>
        {props.desc && (
          <DialogContentText sx={{ mb: 1 }}>{props.desc}</DialogContentText>
        )}
        <RefManager
          addRef={addRef}
          shotId={shotId}
          addUserRef={addUserRef}
          totalRefs={totalRefs}
          pendingIds={pendingIds}
          onPendingChange={setPendingIds}
          onUpload={handleUpload}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
        <Button onClick={handleClose} variant="outlined" color="inherit" size="small">
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          size="small"
          disabled={pendingCount === 0}
        >
          {pendingCount > 0 ? `Add ${pendingCount} Reference${pendingCount !== 1 ? 's' : ''}` : 'Add References'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
